import { useResourceParams, useWarnAboutChange } from '@refinedev/core';
import { Form, FormInstance } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Aplica initialValues + borrador (localStorage) y persiste el formulario completo
 * en cada cambio (con debounce). Pensado para simplicidad y lectura.
 */
interface UseFormDraftInitOptions<T> {
  form: FormInstance<T> | undefined;
  draftKey?: string;
  draftEnabled?: boolean;
  debounceMs?: number;
  initialValues?: Partial<T>;
  excludedDraftFields?: (keyof T | string)[];
}

const useFormDraftInit = <T extends object>({
  form,
  draftKey,
  draftEnabled = true,
  debounceMs = 200,
  initialValues,
  excludedDraftFields = [],
}: UseFormDraftInitOptions<T>) => {
  const [hasInitialSetted, setHasInitialSetted] = useState(false);
  const draftTouchedRef = useRef(false);
  const lastAppliedBaseInitialValuesRef = useRef<Record<string, any> | undefined>(undefined);
  const latestValues = Form.useWatch([], form);
  const { setWarnWhen } = useWarnAboutChange();
  const { resource, action } = useResourceParams();
  const draftKeyValue = useMemo(
    () =>
      draftKey ??
      `${resource?.name}-form-${action}-draft${(initialValues as any)?.id ? `-${(initialValues as any)?.id}` : ''}`,
    [action, draftKey, initialValues, resource],
  );
  const { draftValues, clearDraft: clearStoredDraft } = useDraftData(
    form,
    latestValues,
    draftEnabled,
    draftKeyValue,
    debounceMs,
    hasInitialSetted,
    excludedDraftFields,
  );
  const clearDraft = () => {
    clearStoredDraft();
    draftTouchedRef.current = false;
    setWarnWhen(false);
  };

  // Valores iniciales del form
  const baseInitialValues = useMemo(() => {
    return omitKeys(
      flattenValues(initialValues as Record<string, any> | undefined),
      excludedDraftFields,
    );
  }, [excludedDraftFields, initialValues]);
  // Valores del draft normalizados
  const normalizedDraftValues = useMemo(() => {
    return omitKeys(
      flattenValues(draftValues as Record<string, any> | undefined),
      excludedDraftFields,
    );
  }, [draftValues, excludedDraftFields]);
  // Valores iniciales form + draft
  const mergedInitialValues = useMemo(() => {
    const base = baseInitialValues;
    const draftValuesWithoutExcluded = omitKeys(
      draftValues as Record<string, any> | undefined,
      excludedDraftFields,
    );

    return draftValuesWithoutExcluded ? { ...(base ?? {}), ...draftValuesWithoutExcluded } : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseInitialValues, draftValues]);
  // Detecta si hay cambios entre initialValues y draftValues
  const hasDraftChanges = useMemo(() => {
    if (!draftValues) return false;

    return !areShallowEqual(baseInitialValues, normalizedDraftValues);
  }, [baseInitialValues, draftValues, normalizedDraftValues]);

  // Aplica initialValues + draft una sola vez cuando el form existe.
  useEffect(() => {
    if (!form || !mergedInitialValues) return;

    const baseInitialValuesChanged = !areShallowEqual(
      lastAppliedBaseInitialValuesRef.current,
      baseInitialValues,
    );
    const shouldApplyForFirstTime = !hasInitialSetted;
    // Si backend trae nuevos initialValues (ej: luego de guardar) y no hay draft activo,
    // se reaplican al formulario para evitar desincronización UI vs estado real.
    const shouldReapplyFromBackend =
      hasInitialSetted && baseInitialValuesChanged && !hasDraftChanges;

    if (!shouldApplyForFirstTime && !shouldReapplyFromBackend) {
      return;
    }

    form.setFieldsValue(mergedInitialValues as any);
    setHasInitialSetted(true);
    lastAppliedBaseInitialValuesRef.current = baseInitialValues;
  }, [baseInitialValues, form, hasDraftChanges, hasInitialSetted, mergedInitialValues]);

  // Si hay diferencias entre borrador y server, marcamos los campos como tocados
  // y avisamos al notifier de unsaved changes.
  useEffect(() => {
    if (
      draftTouchedRef.current ||
      !form ||
      !draftEnabled ||
      !hasInitialSetted ||
      !hasDraftChanges ||
      !normalizedDraftValues
    ) {
      return;
    }

    const touchedFields = Object.keys(normalizedDraftValues).map(name => ({
      name,
      touched: true,
    }));

    form.setFields(touchedFields as any);
    setWarnWhen(true);
    draftTouchedRef.current = true;
  }, [draftEnabled, form, hasDraftChanges, hasInitialSetted, normalizedDraftValues, setWarnWhen]);

  return {
    initialValuesSetted: hasInitialSetted,
    formInitialValues: mergedInitialValues,
    hasDraftChanges,
    clearDraft,
  };
};

const useDraftData = <T extends object>(
  form: FormInstance<T> | undefined,
  latestValues: Partial<T> | undefined,
  draftEnabled: boolean,
  draftKey: string,
  debounceMs: number,
  hasInitialSetted: boolean,
  excludedDraftFields: (keyof T | string)[],
) => {
  const timerRef = useRef<number>();
  const persistBlockedRef = useRef(false);
  const lastClearedValuesRef = useRef<Record<string, any> | undefined>(undefined);
  const storage = typeof window === 'undefined' ? undefined : window.sessionStorage;
  const storedDraft = useMemo(() => {
    if (!draftEnabled || !storage) return undefined;

    const raw = storage.getItem(draftKey);

    if (!raw) return undefined;

    try {
      return JSON.parse(raw) as Partial<T>;
    } catch {
      storage.removeItem(draftKey);

      return undefined;
    }
  }, [draftEnabled, draftKey, storage]);
  const [draftValues, setDraftValues] = useState<Partial<T> | undefined>(storedDraft);

  // Persiste el formulario completo en localStorage (debounced).
  useEffect(() => {
    if (!draftEnabled || !form || !storage || !hasInitialSetted || !draftKey) return;

    const currentValuesSnapshot = omitKeys(
      flattenValues(form.getFieldsValue(true) as Record<string, any>),
      excludedDraftFields,
    );

    if (persistBlockedRef.current) {
      // Evita recrear inmediatamente el draft tras clearDraft hasta que exista un cambio real.
      if (areShallowEqual(lastClearedValuesRef.current, currentValuesSnapshot)) {
        return;
      }

      persistBlockedRef.current = false;
      lastClearedValuesRef.current = undefined;
    }

    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const values = form.getFieldsValue(true) as Partial<T>;

      setDraftValues(previousDraftValues => {
        const draft = omitKeys(
          { ...(previousDraftValues as Record<string, any>), ...values } as Record<string, any>,
          excludedDraftFields,
        ) as Partial<T>;

        // Evita re-render loops cuando antd entrega nuevas referencias con mismo contenido.
        if (
          areShallowEqual(
            previousDraftValues as Record<string, any> | undefined,
            draft as Record<string, any> | undefined,
          )
        ) {
          return previousDraftValues;
        }

        cleanupSiblingDraftKeys(storage, draftKey);
        storage.setItem(draftKey, JSON.stringify(draft));

        return draft;
      });
    }, debounceMs);

    return () => window.clearTimeout(timerRef.current);
  }, [
    debounceMs,
    draftEnabled,
    draftKey,
    excludedDraftFields,
    form,
    hasInitialSetted,
    latestValues,
    storage,
  ]);

  const clearDraft = () => {
    if (!storage || !form) return;

    window.clearTimeout(timerRef.current);
    persistBlockedRef.current = true;
    lastClearedValuesRef.current = omitKeys(
      flattenValues(form.getFieldsValue(true) as Record<string, any>),
      excludedDraftFields,
    );

    storage.removeItem(draftKey);
    setDraftValues(undefined);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(timerRef.current);
      clearDraft();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { storedDraft, draftValues, clearDraft };
};

// Helpers
const cleanupSiblingDraftKeys = (storage: Storage, draftKey: string) => {
  const baseKey = getDraftKeyBase(draftKey);

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);

    if (!key || key === draftKey) continue;

    if (key === baseKey || key.startsWith(`${baseKey}-`)) {
      storage.removeItem(key);
    }
  }
};

const getDraftKeyBase = (draftKey: string) => {
  const marker = '-draft';
  const markerIndex = draftKey.lastIndexOf(marker);

  if (markerIndex === -1) return draftKey;

  return draftKey.slice(0, markerIndex + marker.length);
};

const omitKeys = (values?: Record<string, any>, keysToOmit: (string | number | symbol)[] = []) => {
  if (!values) return undefined;

  const omittedKeys = new Set(keysToOmit);

  return Object.fromEntries(Object.entries(values).filter(([key]) => !omittedKeys.has(key)));
};

const flattenValues = (values?: Record<string, any>) => {
  if (!values) return undefined;

  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      Array.isArray(value)
        ? value.map(item => (item && typeof item === 'object' && 'id' in item ? item.id : item))
        : value && typeof value === 'object' && 'id' in value
          ? value.id
          : value,
    ]),
  );
};

const looseEqual = (a: any, b: any) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => looseEqual(v, b[i]));
  }

  return a === b || a == b;
};

const isEmptyObject = (value: any) =>
  value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0;
const areShallowEqual = (a?: Record<string, any>, b?: Record<string, any>): boolean => {
  if ((!a || isEmptyObject(a)) && (!b || isEmptyObject(b))) return true;
  if (!a || !b) return false;

  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    if (!looseEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
};

export default useFormDraftInit;
